import React, { useState, useEffect } from 'react';
import { Store, Package, FileText, TrendingUp, Users, MapPin } from 'lucide-react';
import api, { API_URL } from '../../services/api';
import CatalogueProduits from './CatalogueProduits';

const GestionBoutique = () => {
  const [boutiques, setBoutiques] = useState([]);
  const [boutiqueSelectionnee, setBoutiqueSelectionnee] = useState(null);
  const [activeTab, setActiveTab] = useState('catalogue'); // 'catalogue', 'devis', 'stats'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBoutiques();
  }, []);

  const loadBoutiques = async () => {
    try {
      const response = await api.get(`${API_URL}/boutiques`);
      setBoutiques(response.data);
      if (response.data.length > 0 && !boutiqueSelectionnee) {
        setBoutiqueSelectionnee(response.data[0]);
      }
    } catch (error) {
      console.error('Erreur chargement boutiques:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sélection de la boutique */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <Store className="h-8 w-8 text-indigo-600" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Gestion Boutique</h1>
                <p className="text-sm text-gray-600">Catalogue et ventes</p>
              </div>
            </div>

            {/* Sélecteur de boutique */}
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <label className="text-sm font-medium text-gray-700 flex-shrink-0">Boutique :</label>
              <select
                value={boutiqueSelectionnee?.id || ''}
                onChange={(e) => {
                  const boutique = boutiques.find(b => b.id === parseInt(e.target.value));
                  setBoutiqueSelectionnee(boutique);
                }}
                className="flex-1 sm:flex-none px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {boutiques.map(boutique => (
                  <option key={boutique.id} value={boutique.id}>
                    {boutique.nom} - {boutique.ville}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Onglets */}
          <div className="flex space-x-4 sm:space-x-8 border-t overflow-x-auto">
            <button
              onClick={() => setActiveTab('catalogue')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'catalogue'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Package className="h-5 w-5" />
                <span>Catalogue Produits</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('devis')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'devis'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5" />
                <span>Devis</span>
              </div>
            </button>

            <button
              onClick={() => setActiveTab('stats')}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'stats'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5" />
                <span>Statistiques</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {boutiqueSelectionnee && (
          <>
            {activeTab === 'catalogue' && (
              <CatalogueProduits
                boutiqueId={boutiqueSelectionnee.id}
                boutiqueName={`${boutiqueSelectionnee.nom} - ${boutiqueSelectionnee.ville}`}
              />
            )}

            {activeTab === 'devis' && (
              <ListeDevis boutiqueId={boutiqueSelectionnee.id} />
            )}

            {activeTab === 'stats' && (
              <StatistiquesBoutique boutiqueId={boutiqueSelectionnee.id} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Composant Liste des Devis
const ListeDevis = ({ boutiqueId }) => {
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtreStatut, setFiltreStatut] = useState('');

  useEffect(() => {
    loadDevis();
  }, [boutiqueId, filtreStatut]);

  const loadDevis = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('boutique_id', boutiqueId);
      if (filtreStatut) params.append('statut', filtreStatut);

      const response = await api.get(`${API_URL}/catalogue/devis?${params}`);
      setDevis(response.data);
    } catch (error) {
      console.error('Erreur chargement devis:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      'en_attente': 'bg-yellow-100 text-yellow-800',
      'accepte': 'bg-green-100 text-green-800',
      'refuse': 'bg-red-100 text-red-800',
      'expire': 'bg-gray-100 text-gray-800',
      'converti': 'bg-blue-100 text-blue-800'
    };
    return badges[statut] || 'bg-gray-100 text-gray-800';
  };

  const getStatutLabel = (statut) => {
    const labels = {
      'en_attente': 'En attente',
      'accepte': 'Accepté',
      'refuse': 'Refusé',
      'expire': 'Expiré',
      'converti': 'Converti en vente'
    };
    return labels[statut] || statut;
  };

  const accepterDevis = async (devisId) => {
    if (!window.confirm('Accepter ce devis et créer les ordres de fabrication ?')) return;
    
    try {
      const response = await api.post(`${API_URL}/catalogue/devis/${devisId}/accepter`);
      alert(response.data.message);
      loadDevis();
    } catch (error) {
      console.error('Erreur acceptation devis:', error);
      alert('Erreur lors de l\'acceptation du devis');
    }
  };

  const refuserDevis = async (devisId) => {
    const notes = window.prompt('Raison du refus (optionnel):');
    if (notes === null) return;
    
    try {
      await api.post(`${API_URL}/catalogue/devis/${devisId}/refuser`, { notes });
      alert('Devis refusé');
      loadDevis();
    } catch (error) {
      console.error('Erreur refus devis:', error);
      alert('Erreur lors du refus du devis');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Liste des Devis</h2>
          <select
            value={filtreStatut}
            onChange={(e) => setFiltreStatut(e.target.value)}
            className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Tous les statuts</option>
            <option value="en_attente">En attente</option>
            <option value="accepte">Accepté</option>
            <option value="refuse">Refusé</option>
            <option value="expire">Expiré</option>
            <option value="converti">Converti</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : devis.length > 0 ? (
          <div className="space-y-4">
            {devis.map(d => (
              <div key={d.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{d.numero_devis}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatutBadge(d.statut)}`}>
                        {getStatutLabel(d.statut)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Client: {d.client_nom || 'Non spécifié'} • {d.nombre_lignes} article(s)
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Créé le {new Date(d.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="sm:text-right">
                    <p className="text-2xl font-bold text-indigo-600">
                      {parseFloat(d.total_ttc).toFixed(2)} €
                    </p>
                    <div className="mt-3 flex items-center justify-end space-x-2">
                      {d.statut === 'en_attente' && (
                        <>
                          <button
                            onClick={() => accepterDevis(d.id)}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                          >
                            Accepter
                          </button>
                          <button
                            onClick={() => refuserDevis(d.id)}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                          >
                            Refuser
                          </button>
                        </>
                      )}
                      <button className="text-sm text-indigo-600 hover:text-indigo-800">
                        Détails →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun devis trouvé</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Composant Statistiques Boutique
const StatistiquesBoutique = ({ boutiqueId }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Produits en stock</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">--</p>
            </div>
            <Package className="h-12 w-12 text-indigo-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Devis en attente</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">--</p>
            </div>
            <FileText className="h-12 w-12 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ventes du mois</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">--</p>
            </div>
            <TrendingUp className="h-12 w-12 text-green-600" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques détaillées</h3>
        <p className="text-gray-600">Fonctionnalité en développement...</p>
      </div>
    </div>
  );
};

export default GestionBoutique;
